// Themes API — extracted from api.js (Tier 2 #9a, refactor).
// Module-level _themesCache is kept here so the cache is shared across all
// callers that come through this module OR through the api.js barrel.

import api from './api';

export const fetchThemes = async (opts = {}) => {
  const qs = opts.device_type ? `?device_type=${encodeURIComponent(opts.device_type)}` : '';
  const response = await api.get(`/themes${qs}`);
  return response.data;
};

// Memoized (kept for backward-compat with existing callers).
// NOTE: cache is per-filter — if a caller passes a device_type, the cache key changes.
let _themesCache = new Map();
export const fetchThemesCached = async (opts = {}) => {
  const key = opts.device_type || '_all';
  if (!_themesCache.has(key)) _themesCache.set(key, fetchThemes(opts));
  return _themesCache.get(key);
};
export const invalidateThemesCache = () => { _themesCache = new Map(); };

export const fetchThemeById = async (themeId) => {
  const response = await api.get(`/themes/${themeId}`);
  return response.data;
};

export const createTheme = async (data) => {
  invalidateThemesCache();
  const response = await api.post('/themes', data);
  return response.data;
};

export const updateTheme = async (themeId, data) => {
  invalidateThemesCache();
  const response = await api.put(`/themes/${themeId}`, data);
  return response.data;
};

export const deleteTheme = async (themeId) => {
  invalidateThemesCache();
  const response = await api.delete(`/themes/${themeId}`);
  return response.data;
};

export const duplicateTheme = async (themeId, body = {}) => {
  invalidateThemesCache();
  const response = await api.post(`/themes/${themeId}/duplicate`, body);
  return response.data;
};

// Platform default themes (admin)
export const fetchDefaultThemes   = async ()        => { const r = await api.get(`/themes/defaults`); return r.data; };
export const markThemeAsDefault   = async (themeId) => { const r = await api.put(`/themes/${themeId}/default`); return r.data; };
export const unmarkThemeAsDefault = async (themeId) => { const r = await api.delete(`/themes/${themeId}/default`); return r.data; };

// Per-app per-device theme assignment
export const fetchAppThemes      = async (appId)                        => { const r = await api.get(`/apps/${appId}/themes`); return r.data; };
export const setAppDeviceTheme   = async (appId, deviceType, themeId)   => { const r = await api.put(`/apps/${appId}/theme/${deviceType}`, { theme_id: themeId }); return r.data; };
export const clearAppDeviceTheme = async (appId, deviceType)            => { const r = await api.delete(`/apps/${appId}/theme/${deviceType}`); return r.data; };

// One-shot resolved CSS for an (appId, deviceType) slot.
// Server-side resolution: app-assigned → platform default → 204 (no theme).
// Returns the CSS string when resolved, or "" when the server has nothing to send.
export const fetchAppDeviceThemeCss = async (appId, deviceType) => {
  const r = await api.get(`/apps/${appId}/theme/${deviceType}/css`, {
    responseType: 'text',
    // 204 No Content is treated as a normal success — no theme assigned/default.
    validateStatus: (s) => s === 200 || s === 204,
  });
  return r.status === 204 ? '' : r.data;
};

// ── Bootstrap themes (HTML surfaces) ──
// A second catalog (category='bootstrap'); assignment is per-app, device-agnostic.
export const fetchBootstrapThemes     = async ()               => { const r = await api.get(`/themes?category=bootstrap`); return r.data; };
export const fetchAppBootstrapTheme   = async (appId)          => { const r = await api.get(`/apps/${appId}/bootstrap-theme`); return r.data; };
export const setAppBootstrapTheme     = async (appId, themeId) => { const r = await api.put(`/apps/${appId}/bootstrap-theme`, { theme_id: themeId }); return r.data; };
export const clearAppBootstrapTheme   = async (appId)          => { const r = await api.delete(`/apps/${appId}/bootstrap-theme`); return r.data; };

// Backward-compat alias — callers should migrate to invalidateThemesCache()
export const clearThemesCache = invalidateThemesCache;

// ── App layout settings (per-device canvas width) ────────────────────────────
// Not a theme, but the same (app, device) shape and edited on the same page, so
// it lives beside the theme-assignment helpers rather than in its own module.
// The response is always { canvas_width: { [device]: px } } — never null.
export const fetchAppLayoutSettings = async (appId) => {
  const r = await api.get(`/apps/${appId}/layout-settings`);
  return r.data;
};

// Replaces canvas_width wholesale. Omit a device (or send 0) to clear it back
// to the built-in default.
export const updateAppLayoutSettings = async (appId, canvasWidth) => {
  const r = await api.put(`/apps/${appId}/layout-settings`, { canvas_width: canvasWidth });
  return r.data;
};

// ── Layout rescale ───────────────────────────────────────────────────────────
// Adjust element positions for a new canvas width. The transform lives ONLY in
// studio-backend (services.RescaleLayout) — the frontend posts a layout and
// renders what comes back, it never transforms one itself.

// Stateless: transforms the layout the editor currently holds (unsaved edits
// included) and returns { layout, changed, overflows }. Persisting is the
// caller's job, through the normal screen save.
export const rescaleLayout = async (appId, { layout, fromWidth, toWidth, anchorOverrides }) => {
  const r = await api.post(`/apps/${appId}/layout/rescale`, {
    layout,
    from_width: fromWidth,
    to_width: toWidth,
    ...(anchorOverrides ? { anchor_overrides: anchorOverrides } : {}),
  });
  return r.data;
};

// App-wide batch over every screen that INHERITS this device's width.
// `apply: false` (the default) is a dry run — it reports what WOULD change and
// writes nothing, which is what the confirmation dialog previews.
export const batchRescaleLayouts = async (appId, { device, toWidth, apply = false }) => {
  const r = await api.post(`/apps/${appId}/layout-settings/rescale`, {
    device,
    to_width: toWidth,
    apply,
  });
  return r.data;
};
